import { useRouter } from "#app";
import type { RouteLocationRaw } from "vue-router";

export interface PostMessageEvent {
  name: string;
  payload?: Record<string, unknown>;
}

export interface NavigationIntent {
  intent: string;
  payload?: Record<string, unknown>;
}

export interface RouteChangeEvent {
  name: "route-change";
  payload: {
    from: {
      name: string;
      params: Record<string, unknown>;
      path: string;
    };
    to: {
      name: string;
      params: Record<string, unknown>;
      path: string;
    };
  };
}

// Intent to route mapping
const INTENT_ROUTE_MAP: Record<string, RouteLocationRaw> = {
  "go-home": { name: "home" },
  "go-order": { name: "order" },
  "go-locations": { name: "locations" },
  "go-loyalty-program": { name: "loyalty-program" },
  "go-account": { name: "my-account" },
  "go-back": "back",
};

export const useDirectApp = () => {
  const router = useRouter();

  const reactNativeWebview = window?.ReactNativeWebView;

  const postMessage = (event: PostMessageEvent) => {
    if (
      !reactNativeWebview ||
      typeof reactNativeWebview.postMessage !== "function"
    ) {
      return;
    }

    const message = JSON.stringify({
      ...event,
      timestamp: Date.now(),
    });

    reactNativeWebview.postMessage(message);
  };

  const notifyRouteChange = (
    from: RouteChangeEvent["payload"]["from"],
    to: RouteChangeEvent["payload"]["to"],
  ) => {
    const event: RouteChangeEvent = {
      name: "route-change",
      payload: {
        from,
        to,
      },
    };

    postMessage(event);
  };

  const handleNavigationIntent = (intent: NavigationIntent) => {
    if (!reactNativeWebview) return;

    const routeConfig = INTENT_ROUTE_MAP[intent.intent] ?? {};

    if (!routeConfig) {
      return;
    }

    try {
      if (routeConfig === "back") {
        router.back();
      } else {
        const finalRoute: RouteLocationRaw = {
          ...(typeof routeConfig === "object" ? routeConfig : {}),
          ...(intent.payload || {}),
        };

        router.push(finalRoute);
      }
    } catch {
      //
    }
  };

  const setupMessageListener = () => {
    if (!reactNativeWebview) return;

    window.handleDirectAppMessage = function (data: unknown) {
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        if (parsed.type === "navigation-intent") {
          window.dispatchEvent(
            new CustomEvent("fromRN", {
              detail: parsed,
            }),
          );
        }
      } catch {
        // Optionally log or handle error
      }
    };

    const handleMessage = (event: CustomEventInit) => {
      if (event.detail.type !== "navigation-intent") return;
      handleNavigationIntent({ intent: event.detail.intent ?? "" });
    };

    window.addEventListener("fromRN", handleMessage);

    return () => {
      window.removeEventListener("fromRN", handleMessage);
    };
  };

  return {
    postMessage,
    notifyRouteChange,
    handleNavigationIntent,
    setupMessageListener,
  };
};
